/**
 * Phase 14.3 — Issue Gold from the Gold Ledger.
 *
 * A single dialog lets the user issue gold to one or more goldsmiths in a
 * single submission. Per goldsmith we collect a list of lines where each
 * line is either tied to an order or unattributed. On submit we fan out
 * a separate ``POST /goldsmith-issues/`` per goldsmith so each goldsmith
 * gets its own Issue Memo (per the user's explicit requirement).
 *
 * UX rules (per user):
 *   • Issue memo generated per goldsmith
 *   • Unattributed lines supported
 *   • Hard block when total grams 999-equivalent exceeds available gold
 *   • Purity selected from Gold Purities master for every line
 *   • Current company gold rate auto-resolved by backend at submit time
 */

import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { extractError } from '@/lib/errors';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, ArrowUpFromLine, AlertTriangle, ShieldCheck, UserPlus } from 'lucide-react';
import { formatGrams } from '@/lib/format';
import { GoldsmithFormDialog } from './GoldsmithFormDialog';

const UNATTRIBUTED = '__unattributed__';

function makeLine() {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    order_id: UNATTRIBUTED,
    requirement_id: '',
    purity_id: '',
    grams: '',
    note: '',
  };
}

function makeBlock() {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    goldsmith_id: '',
    lines: [makeLine()],
  };
}

export function GoldIssueDialog({
  open, onOpenChange, companyId,
  goldsmiths, purities, availableGrams999, onIssued,
  onGoldsmithCreated,
}) {
  const [blocks, setBlocks] = useState([makeBlock()]);
  const [submitting, setSubmitting] = useState(false);
  const [ordersByGoldsmith, setOrdersByGoldsmith] = useState({}); // gid -> [orders]
  // Phase 15 — inline goldsmith creation
  const [newGsOpen, setNewGsOpen] = useState(false);
  const [pendingGsBlockId, setPendingGsBlockId] = useState(null);

  // Reset state every time the dialog opens
  useEffect(() => {
    if (open) {
      setBlocks([makeBlock()]);
      setSubmitting(false);
      setOrdersByGoldsmith({});
      setNewGsOpen(false);
      setPendingGsBlockId(null);
    }
  }, [open]);

  // Build a purity lookup so we can compute 999-equivalent for the running total
  const purityById = useMemo(() => {
    const m = {};
    (purities || []).forEach((p) => { m[p.id] = p; });
    return m;
  }, [purities]);

  // Pending GOLD requirements per order (only "open" or "partial" status with
  // remaining grams). Indexed by order_id for fast lookup.
  const goldReqsByOrder = useMemo(() => {
    const m = {};
    Object.entries(ordersByGoldsmith).forEach(([, orders]) => {
      (orders || []).forEach((o) => {
        const reqs = (o.requirements || []).filter((r) => {
          if (r.material_type !== 'gold') return false;
          if (r.status === 'completed') return false;
          const remaining = Number(r.required_grams || 0) - Number(r.fulfilled_grams || 0);
          return remaining > 0.0005;
        });
        m[o.id] = reqs;
      });
    });
    return m;
  }, [ordersByGoldsmith]);

  // Best purity match for a requirement's required_purity (numeric percent)
  const findPurityByPercent = (percent) => {
    if (percent == null) return null;
    const p = (purities || []).find((x) => Math.abs(Number(x.percent || 0) - Number(percent)) < 0.05);
    return p || null;
  };

  // Fetch orders for a goldsmith on demand (open status only). We
  // deliberately swallow errors here — the dropdown simply stays empty
  // and the user can still choose "Unattributed".
  const loadOrdersFor = async (gid) => {
    if (!gid || ordersByGoldsmith[gid] !== undefined) return;
    // Mark as in-flight to dedupe rapid re-selections
    setOrdersByGoldsmith((prev) => ({ ...prev, [gid]: [] }));
    try {
      // NOTE: include the trailing slash explicitly. The ingress otherwise
      // 307-redirects `/api/orders` -> `/api/orders/`, and that redirect
      // drops the scheme back to ``http://`` which the browser then blocks
      // as Mixed Content. (Hit during Phase 15 bug investigation.)
      const { data } = await api.get('/orders/', {
        params: { company_id: companyId, goldsmith_id: gid, status: 'open' },
      });
      setOrdersByGoldsmith((prev) => ({ ...prev, [gid]: Array.isArray(data) ? data : [] }));
    } catch (e) {
      // Non-blocking: leave the empty array so Unattributed still works.
      console.warn('[GoldIssueDialog] orders fetch failed', e);
    }
  };

  // Running total (grams of pure 999 equivalent across every line everywhere)
  const totalGrams999 = useMemo(() => {
    let t = 0;
    for (const b of blocks) {
      for (const ln of b.lines) {
        const g = Number(ln.grams || 0);
        const p = purityById[ln.purity_id];
        const pct = p ? Number(p.percent || 0) : 0;
        if (g > 0 && pct > 0) {
          t += (g * pct) / 100;
        }
      }
    }
    return t;
  }, [blocks, purityById]);

  const overBudget = totalGrams999 > (availableGrams999 || 0) + 1e-9;

  // Validation gate (all goldsmith blocks need a goldsmith + valid lines)
  const validationError = useMemo(() => {
    if (overBudget) {
      return `Total 999-equivalent ${formatGrams(totalGrams999)}g exceeds available ${formatGrams(availableGrams999)}g.`;
    }
    const seen = new Set();
    for (const b of blocks) {
      if (!b.goldsmith_id) return 'Select a goldsmith for every block.';
      if (seen.has(b.goldsmith_id)) {
        return 'The same goldsmith is selected in multiple blocks. Combine them into one block instead.';
      }
      seen.add(b.goldsmith_id);
      if (!b.lines.length) return 'Each goldsmith needs at least one line.';
      for (const ln of b.lines) {
        if (!ln.purity_id) return 'Pick a purity for every line.';
        const g = Number(ln.grams || 0);
        if (!(g > 0)) return 'Grams must be greater than zero for every line.';
      }
    }
    return null;
  }, [blocks, overBudget, totalGrams999, availableGrams999]);

  // Mutators
  const updateBlock = (bid, patch) => setBlocks((bs) => bs.map((b) => (b.id === bid ? { ...b, ...patch } : b)));
  const updateLine = (bid, lid, patch) => setBlocks((bs) => bs.map((b) =>
    b.id === bid ? { ...b, lines: b.lines.map((ln) => (ln.id === lid ? { ...ln, ...patch } : ln)) } : b,
  ));
  const addLine = (bid) => setBlocks((bs) => bs.map((b) => (b.id === bid ? { ...b, lines: [...b.lines, makeLine()] } : b)));
  const removeLine = (bid, lid) => setBlocks((bs) => bs.map((b) =>
    b.id === bid ? { ...b, lines: b.lines.length > 1 ? b.lines.filter((ln) => ln.id !== lid) : b.lines } : b,
  ));
  const addBlock = () => setBlocks((bs) => [...bs, makeBlock()]);
  const removeBlock = (bid) => setBlocks((bs) => (bs.length > 1 ? bs.filter((b) => b.id !== bid) : bs));

  // Submit — fan out one POST /goldsmith-issues/ per block (per goldsmith)
  const submit = async () => {
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setSubmitting(true);
    const memos = [];
    const failures = [];
    try {
      for (const b of blocks) {
        const payload = {
          goldsmith_id: b.goldsmith_id,
          company_id: companyId,
          reference: '',
          note: '',
          lines: b.lines.map((ln) => {
            const order = ordersByGoldsmith[b.goldsmith_id]?.find((o) => o.id === ln.order_id);
            return {
              material_type: 'gold',
              purity_id: ln.purity_id,
              grams: Number(ln.grams),
              order_id: ln.order_id === UNATTRIBUTED ? null : ln.order_id,
              order_no: order?.order_no || null,
              requirement_id: (ln.order_id === UNATTRIBUTED || !ln.requirement_id || ln.requirement_id === '__none__') ? null : ln.requirement_id,
              note: ln.note || '',
            };
          }),
        };
        try {
          const { data } = await api.post('/goldsmith-issues/', payload);
          memos.push({
            goldsmith_id: b.goldsmith_id,
            goldsmith_name: goldsmiths.find((g) => g.id === b.goldsmith_id)?.name || '(unknown)',
            issue_id: data?.id,
            memo_no: data?.issue_memo_no || data?.issue_no,
          });
        } catch (e) {
          failures.push({
            goldsmith_id: b.goldsmith_id,
            goldsmith_name: goldsmiths.find((g) => g.id === b.goldsmith_id)?.name || '(unknown)',
            error: extractError(e, 'Failed'),
          });
        }
      }
      if (memos.length > 0 && failures.length === 0) {
        toast.success(
          memos.length === 1
            ? `Gold issued. Memo ${memos[0].memo_no} created.`
            : `Gold issued to ${memos.length} goldsmiths. Memos: ${memos.map((m) => m.memo_no).join(', ')}`,
        );
        onOpenChange(false);
        onIssued?.(memos);
      } else if (memos.length > 0 && failures.length > 0) {
        toast.warning(
          `${memos.length} memos created, ${failures.length} failed. Failures: ${failures.map((f) => `${f.goldsmith_name}: ${f.error}`).join('; ')}`,
        );
        onIssued?.(memos);
      } else {
        toast.error(`All issues failed: ${failures.map((f) => `${f.goldsmith_name}: ${f.error}`).join('; ')}`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const lineRemaining = (availableGrams999 || 0) - totalGrams999;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="gold-issue-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[var(--ink-navy)]">
            <ArrowUpFromLine size={18} className="text-amber-600" />
            Issue Gold to Goldsmith(s)
          </DialogTitle>
          <DialogDescription>
            One Issue Memo is generated <strong>per goldsmith</strong>. Each
            line is either tied to an order or unattributed. The system uses
            your current company gold rate at the time of submit.
          </DialogDescription>
        </DialogHeader>

        {/* Balance strip — surfaces the hard-block rule prominently */}
        <Card
          className={`p-3 flex items-center justify-between border ${
            overBudget ? 'border-rose-300 bg-rose-50' : 'border-emerald-200 bg-emerald-50'
          }`}
          data-testid="gold-issue-balance-strip"
        >
          <div className="flex items-center gap-2 text-sm">
            {overBudget ? (
              <AlertTriangle size={16} className="text-rose-600" />
            ) : (
              <ShieldCheck size={16} className="text-emerald-700" />
            )}
            <span className="text-slate-700">
              Available <strong className="text-[var(--ink-navy)]" data-testid="gold-available-grams">{formatGrams(availableGrams999)}g</strong> · This issue
              <strong className={`mx-1 ${overBudget ? 'text-rose-700' : 'text-emerald-700'}`} data-testid="gold-issue-total">{formatGrams(totalGrams999)}g</strong>
              · Remaining
              <strong className={`ml-1 ${lineRemaining < 0 ? 'text-rose-700' : 'text-[var(--ink-navy)]'}`} data-testid="gold-issue-remaining">
                {formatGrams(Math.max(lineRemaining, 0))}g
              </strong>
            </span>
          </div>
          {overBudget && (
            <Badge className="bg-rose-600 text-white" data-testid="gold-issue-over-budget-badge">
              Insufficient gold
            </Badge>
          )}
        </Card>

        <div className="space-y-4">
          {blocks.map((b, bi) => {
            const gs = goldsmiths.find((g) => g.id === b.goldsmith_id);
            const orders = ordersByGoldsmith[b.goldsmith_id] || [];
            return (
              <Card key={b.id} className="p-3 border-slate-200" data-testid={`gold-issue-block-${bi}`}>
                <div className="flex items-center gap-3 mb-3">
                  <Badge variant="secondary" className="text-[10px]">
                    Goldsmith {bi + 1}
                  </Badge>
                  <div className="flex-1">
                    <SearchableSelect
                      items={goldsmiths}
                      value={b.goldsmith_id}
                      onValueChange={(v) => {
                        updateBlock(b.id, { goldsmith_id: v || '' });
                        if (v) loadOrdersFor(v);
                      }}
                      itemToLabel={(g) => g.name}
                      itemToSubLabel={(g) => [g.phone, g.email].filter(Boolean).join(' · ') || null}
                      searchKeys={['name', 'phone', 'email']}
                      placeholder={goldsmiths.length === 0 ? 'No goldsmiths — click + to create' : 'Select goldsmith…'}
                      searchPlaceholder="Search goldsmith…"
                      disabled={goldsmiths.length === 0}
                      testId={`gold-issue-block-${bi}-goldsmith-select`}
                    />
                  </div>
                  {/* Phase 15 — inline "New Goldsmith" */}
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="border-slate-200 text-[var(--ink-navy)] hover:bg-slate-50"
                    onClick={() => { setPendingGsBlockId(b.id); setNewGsOpen(true); }}
                    title="Create a new goldsmith"
                    data-testid={`gold-issue-block-${bi}-new-goldsmith`}
                  >
                    <UserPlus size={14} />
                  </Button>
                  {blocks.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-rose-600 hover:bg-rose-50"
                      onClick={() => removeBlock(b.id)}
                      data-testid={`gold-issue-block-${bi}-remove`}
                      title="Remove this goldsmith block"
                    >
                      <Trash2 size={14} />
                    </Button>
                  )}
                </div>

                {/* Lines */}
                <div className="space-y-2">
                  {b.lines.map((ln, li) => {
                    const purity = purityById[ln.purity_id];
                    const grams = Number(ln.grams || 0);
                    const pct = purity ? Number(purity.percent || 0) : 0;
                    const line999 = grams > 0 && pct > 0 ? (grams * pct) / 100 : 0;
                    const isAttributed = ln.order_id && ln.order_id !== UNATTRIBUTED;
                    const lineGoldReqs = isAttributed ? (goldReqsByOrder[ln.order_id] || []) : [];
                    const selectedReq = lineGoldReqs.find((r) => r.id === ln.requirement_id);
                    return (
                      <div
                        key={ln.id}
                        className="p-3 rounded-lg bg-white border border-slate-200 space-y-2"
                        data-testid={`gold-issue-block-${bi}-line-${li}`}
                      >
                        {/* Row 1 — Attribution + Requirement (when applicable) */}
                        <div className="grid grid-cols-12 gap-2">
                          <div className={isAttributed && lineGoldReqs.length > 0 ? 'col-span-6' : 'col-span-12'}>
                            <Label className="text-[10px] text-slate-500 uppercase tracking-wide">Attribution</Label>
                            {(() => {
                              const orderOptions = orders.map((o) => {
                                const hasGoldReq = (goldReqsByOrder[o.id] || []).length > 0;
                                return {
                                  id: o.id,
                                  label: `${o.order_no} — ${o.title || 'Untitled'}${hasGoldReq ? '  •  Gold req pending' : ''}`,
                                  order_no: o.order_no,
                                  title: o.title,
                                };
                              });
                              const items = [{ id: UNATTRIBUTED, label: 'Unattributed (no order)' }, ...orderOptions];
                              return (
                                <SearchableSelect
                                  items={items}
                                  value={ln.order_id || UNATTRIBUTED}
                                  onValueChange={(v) => {
                                    const orderId = v || UNATTRIBUTED;
                                    // When the order changes, reset requirement and try to
                                    // pre-fill the purity from the requirement (if exactly 1
                                    // pending gold requirement exists).
                                    const reqs = (goldReqsByOrder[orderId] || []);
                                    let nextPurity = ln.purity_id;
                                    let nextReq = '';
                                    if (orderId !== UNATTRIBUTED && reqs.length === 1) {
                                      nextReq = reqs[0].id;
                                      const matched = findPurityByPercent(reqs[0].required_purity);
                                      if (matched) nextPurity = matched.id;
                                    }
                                    updateLine(b.id, ln.id, { order_id: orderId, requirement_id: nextReq, purity_id: nextPurity });
                                  }}
                                  itemToValue={(it) => it.id}
                                  itemToLabel={(it) => it.label}
                                  searchKeys={['label', 'order_no', 'title']}
                                  placeholder="Unattributed"
                                  searchPlaceholder="Search order…"
                                  disabled={!b.goldsmith_id}
                                  testId={`gold-issue-block-${bi}-line-${li}-order`}
                                />
                              );
                            })()}
                          </div>
                          {isAttributed && lineGoldReqs.length > 0 && (
                            <div className="col-span-6">
                              <Label className="text-[10px] text-slate-500 uppercase tracking-wide">
                                Gold Requirement
                                <span className="ml-1 text-emerald-700 normal-case font-medium">
                                  ({lineGoldReqs.length} pending)
                                </span>
                              </Label>
                              {(() => {
                                const reqOptions = lineGoldReqs.map((r) => {
                                  const remaining = Math.max(0, Number(r.required_grams || 0) - Number(r.fulfilled_grams || 0));
                                  const purityLabel = r.required_purity ? ` @ ${Number(r.required_purity).toFixed(2)}%` : '';
                                  const reqLabel = (r.description || r.classification_label || 'Gold requirement').toString();
                                  return { id: r.id, label: `${reqLabel} · Pending ${remaining.toFixed(3)}g${purityLabel}` };
                                });
                                const items = [{ id: '__none__', label: '— Not attributed to a requirement —' }, ...reqOptions];
                                return (
                                  <SearchableSelect
                                    items={items}
                                    value={ln.requirement_id || '__none__'}
                                    onValueChange={(v) => {
                                      const reqId = !v || v === '__none__' ? '__none__' : v;
                                      const req = lineGoldReqs.find((r) => r.id === reqId);
                                      const matched = req ? findPurityByPercent(req.required_purity) : null;
                                      updateLine(b.id, ln.id, {
                                        requirement_id: reqId === '__none__' ? '' : reqId,
                                        ...(matched ? { purity_id: matched.id } : {}),
                                      });
                                    }}
                                    itemToValue={(it) => it.id}
                                    itemToLabel={(it) => it.label}
                                    searchKeys={['label']}
                                    placeholder="(not attributed to a requirement)"
                                    searchPlaceholder="Search requirement…"
                                    testId={`gold-issue-block-${bi}-line-${li}-requirement`}
                                  />
                                );
                              })()}
                              {selectedReq && (
                                <div className="text-[10px] text-emerald-700 mt-1" data-testid={`gold-issue-block-${bi}-line-${li}-req-info`}>
                                  Pending: {Math.max(0, Number(selectedReq.required_grams || 0) - Number(selectedReq.fulfilled_grams || 0)).toFixed(3)}g · Required purity: {selectedReq.required_purity ? `${Number(selectedReq.required_purity).toFixed(2)}%` : '—'}
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Row 2 — Purity + Grams + Note + 999 + remove */}
                        <div className="grid grid-cols-12 gap-2 items-end">
                          <div className="col-span-4">
                            <Label className="text-[10px] text-slate-500 uppercase tracking-wide">Purity</Label>
                            <SearchableSelect
                              items={purities}
                              value={ln.purity_id}
                              onValueChange={(v) => updateLine(b.id, ln.id, { purity_id: v || '' })}
                              itemToLabel={(p) => `${p.name} (${Number(p.percent).toFixed(2)}%)`}
                              searchKeys={['name']}
                              placeholder="Select…"
                              searchPlaceholder="Search purity…"
                              disabled={purities.length === 0}
                              testId={`gold-issue-block-${bi}-line-${li}-purity`}
                            />
                          </div>
                          <div className="col-span-2">
                            <Label className="text-[10px] text-slate-500 uppercase tracking-wide">Grams</Label>
                            <Input
                              type="number"
                              step="0.001"
                              min="0"
                              value={ln.grams}
                              onChange={(e) => updateLine(b.id, ln.id, { grams: e.target.value })}
                              className="bg-white"
                              data-testid={`gold-issue-block-${bi}-line-${li}-grams`}
                            />
                          </div>
                          <div className="col-span-3">
                            <Label className="text-[10px] text-slate-500 uppercase tracking-wide">Note</Label>
                            <Input
                              value={ln.note}
                              onChange={(e) => updateLine(b.id, ln.id, { note: e.target.value })}
                              placeholder="optional"
                              className="bg-white"
                              data-testid={`gold-issue-block-${bi}-line-${li}-note`}
                            />
                          </div>
                          <div className="col-span-2 flex flex-col items-end justify-end pb-1.5">
                            <div className="text-[10px] text-slate-500 uppercase tracking-wide leading-tight">999 Equiv</div>
                            <div className="font-mono text-[12px] tabular-nums text-[var(--ink-navy)] font-semibold" data-testid={`gold-issue-block-${bi}-line-${li}-999`}>
                              {formatGrams(line999)}g
                            </div>
                          </div>
                          <div className="col-span-1 flex items-end justify-end pb-2">
                            {b.lines.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeLine(b.id, ln.id)}
                                className="text-rose-500 hover:text-rose-700"
                                title="Remove line"
                                data-testid={`gold-issue-block-${bi}-line-${li}-remove`}
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addLine(b.id)}
                    className="text-xs"
                    data-testid={`gold-issue-block-${bi}-add-line`}
                    disabled={!b.goldsmith_id}
                  >
                    <Plus size={12} className="mr-1" /> Add line
                  </Button>
                </div>
              </Card>
            );
          })}
          <Button
            type="button"
            variant="outline"
            onClick={addBlock}
            data-testid="gold-issue-add-block"
            className="border-dashed"
          >
            <Plus size={14} className="mr-1.5" /> Add another goldsmith
          </Button>
        </div>

        {validationError && (
          <div
            className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2 flex items-start gap-2"
            data-testid="gold-issue-validation-error"
          >
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span>{validationError}</span>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="gold-issue-cancel"
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || !!validationError}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="gold-issue-submit"
          >
            {submitting ? 'Issuing…' : `Issue Gold${blocks.length > 1 ? ` to ${blocks.length} goldsmiths` : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Phase 15 — Inline "New Goldsmith" dialog. Sibling of the main
          DialogContent so it sits above the Issue Gold dialog when open. */}
      <GoldsmithFormDialog
        open={newGsOpen}
        onOpenChange={setNewGsOpen}
        companyId={companyId}
        onCreated={(newGs) => {
          // Auto-select the freshly-created goldsmith into the block that
          // opened the dialog so the user can continue without re-clicking.
          if (pendingGsBlockId) {
            updateBlock(pendingGsBlockId, { goldsmith_id: newGs.id });
            loadOrdersFor(newGs.id);
          }
          // Notify parent so its `goldsmiths` prop refreshes (Select will
          // then show the new entry).
          if (onGoldsmithCreated) onGoldsmithCreated(newGs);
          setPendingGsBlockId(null);
        }}
      />
    </Dialog>
  );
}
